import { useMemo } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Snackbar } from '@atoms';
import { useURLparams } from '@hooks';
import { FullWidthContent, MainMenuLayout } from '@layouts';
import { Table } from '@molecules';
import { SelectSegmentCard } from '@organisms';
import { t } from '@config/i18n';
import { DEFAULT_ROWS_PER_PAGE } from '@modules/usersManagment/constants';
import { columns } from './constants';
import useDeleteLimit from './data/useDeleteLimit';
import { TransactionLimit, useGetLimitsBySegment } from './data/useGetLimitsBySegment';

/**
 * Limits management screen
 */
const LimitsManagement = () => {
  const { params, setURLparams } = useURLparams<{
    page: string;
    limit: string;
    segment: string;
  }>({
    page: '1',
    limit: String(DEFAULT_ROWS_PER_PAGE),
  });

  const {
    data: response,
    isLoading,
    handlePageChange,
    handleRowsPerPageChange,
    handleRefresh,
  } = useGetLimitsBySegment({ params, setURLparams, segmentId: params?.segment });

  const {
    submit: deleteLimit,
    isModalOpen: isDeleteModalOpen,
    setIsModalOpen: setIsDeleteModalOpen,
    handlePreSubmit,
    limitToDelete,
    alertMessage,
    isSuccess,
    setIsSuccess,
  } = useDeleteLimit({ handleRefresh: handleRefresh as () => void });
  const navigate = useNavigate();

  const cols = useMemo(() => columns(handlePreSubmit), [handlePreSubmit]);

  /**
   * Go to limit detail screen
   */
  function navigateToDetail(trx: TransactionLimit) {
    navigate(`/gestion-de-limites/${trx.id}`);
  }
  return (
    <MainMenuLayout>
      <FullWidthContent>
        <SelectSegmentCard
          setSegment={(value) => {
            setURLparams({ ...params, segment: value });
          }}
          segment={params?.segment}
          showSubSegments
        />
        <Table
          columns={cols}
          count={response?.totalCount || 0}
          isLoading={isLoading}
          items={response?.page}
          page={params?.page || 1}
          rowsPerPage={params?.limit || DEFAULT_ROWS_PER_PAGE}
          //TODO: Cast the typing to avoid build errors.
          onPageChange={handlePageChange as () => void}
          onRowsPerPageChange={handleRowsPerPageChange as () => void}
          onItemClick={navigateToDetail}
        />
        <Alert
          variant='warning'
          modalVisible={isDeleteModalOpen}
          handleClose={() => setIsDeleteModalOpen(false)}
          handleSubmit={deleteLimit}
          closeButtonText='COMMON:CANCEL'
          title={t('LIMITS:DELETE_MODAL:TITLE')}
          submitLoading={isLoading}
          content={
            <Trans
              i18nKey='LIMITS:DELETE_MODAL:DESCRIPTION'
              values={{
                description: limitToDelete?.description || '',
                type: limitToDelete?.transactionType || '',
              }}
              components={{ bold: <strong /> }}
            />
          }
        />
        <Snackbar
          open={isSuccess}
          message={t(alertMessage.title)}
          severity='success'
          handleClose={() => setIsSuccess(false)}
        />
      </FullWidthContent>
    </MainMenuLayout>
  );
};
export default LimitsManagement;
